'use client'
import {
  Button_default
} from "./chunk-EGOGHW2A.mjs";
import {
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-BTG5EALA.mjs";

// src/Toast/Toast.tsx
import { useEffect, useImperativeHandle, useRef, useState } from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { twMerge } from "tailwind-merge";
import * as Portal from "@radix-ui/react-portal";

// src/Toast/Toast.classes.tsx
var CALSSES = {
  success: "bg-green-600",
  error: "bg-red-600",
  warning: "bg-yellow-600",
  info: "bg-blue-600"
};
var ANIMATION = {
  right: "data-[state=open]:slide-in-from-right-full data-[state=closed]:slide-out-to-right-full data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]",
  left: "data-[state=open]:slide-in-from-left-full data-[state=closed]:slide-out-to-left-full data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]",
  up: "data-[state=open]:slide-in-from-top-full data-[state=closed]:slide-out-to-top-full data-[swipe=cancel]:translate-y-0 data-[swipe=end]:translate-y-[var(--radix-toast-swipe-end-y)] data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)]",
  down: "data-[state=open]:slide-in-from-bottom-full data-[state=closed]:slide-out-to-bottom-full data-[swipe=cancel]:translate-y-0 data-[swipe=end]:translate-y-[var(--radix-toast-swipe-end-y)] data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)]"
};
var VIEWPORT = {
  right: "right-0 top-20",
  left: "left-0 top-20",
  up: "top-8",
  down: "bottom-8"
};
var POSITION = {
  auto: "",
  center: "left-1/2 -translate-x-1/2"
};

// src/Toast/Toast.icons.tsx
var ICONS = {
  success: "M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM16.4072 9.35928L10.5742 16.4885L7.08579 13L8.5 11.5858L10.4258 13.5115L14.8593 8.0928L16.4072 9.35928Z",
  error: "M11.9999 1.04102L23.2566 19.9997H0.743164L11.9999 1.04102ZM4.25664 17.9997H19.7432L11.9999 4.95845L4.25664 17.9997ZM12.9999 8.99973V13.9997H10.9999V8.99973H12.9999ZM12.9999 14.9897V16.9997H10.9999V14.9897H12.9999Z",
  warning: "M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM16.4072 9.35928L10.5742 16.4885L7.08579 13L8.5 11.5858L10.4258 13.5115L14.8593 8.0928L16.4072 9.35928Z",
  info: "M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM16.4072 9.35928L10.5742 16.4885L7.08579 13L8.5 11.5858L10.4258 13.5115L14.8593 8.0928L16.4072 9.35928Z"
};

// src/Toast/Toast.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var DEFAULT_TYPE = "success";
var Toast = (_a) => {
  var _b = _a, { className, swipeDirection, position, ref, multiple = false } = _b, props = __objRest(_b, ["className", "swipeDirection", "position", "ref", "multiple"]);
  const [toasts, setToasts] = useState([]);
  const direction = swipeDirection || "right";
  const pos = position || "auto";
  const timeoutRef = useRef({});
  const generateToast = (id, props2) => {
    return __spreadProps(__spreadValues({
      id
    }, props2), {
      open: true,
      swipeable: typeof props2.swipeable == "boolean" ? props2.swipeable : true,
      dismissible: typeof props2.dismissible == "boolean" ? props2.dismissible : true,
      type: props2.type || DEFAULT_TYPE
    });
  };
  useImperativeHandle(ref, () => ({
    publish: (props2) => {
      const toastId = Math.random().toString(36).substring(7);
      if (typeof props2 === "function") {
        return setToasts((prev) => {
          const newToast = props2({ id: toastId });
          const generated = generateToast(toastId, newToast);
          return multiple ? prev.concat(generated) : [generated];
        });
      }
      setToasts((prev) => {
        const generated = generateToast(toastId, props2);
        return multiple ? prev.concat(generated) : [generated];
      });
    },
    dismiss: handleClose
  }));
  const handleClose = (id) => {
    setToasts((prev) => prev.map((toast) => toast.id === id ? __spreadProps(__spreadValues({}, toast), { open: false }) : toast));
    timeoutRef.current[id] = setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 2e3);
  };
  useEffect(() => {
    const pending = timeoutRef.current;
    return () => {
      Object.values(pending).forEach((timeout) => clearTimeout(timeout));
    };
  }, []);
  return /* @__PURE__ */ jsx(Portal.Root, { children: /* @__PURE__ */ jsxs(ToastPrimitive.Provider, __spreadProps(__spreadValues({}, props), { swipeDirection: direction, children: [
    toasts.map((toast) => {
      const type = toast.type || DEFAULT_TYPE;
      const icon = ICONS[type];
      return /* @__PURE__ */ jsxs(
        ToastPrimitive.Root,
        {
          className: twMerge(
            CALSSES[type],
            ANIMATION[direction],
            "flex items-center gap-1 rounded-xl p-4",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in data-[swipe=cancel]:transition-[transform_200ms_ease-out]",
            className
          ),
          duration: toast.duration,
          open: toast.open,
          onOpenChange: (isOpen) => {
            if (!isOpen) {
              handleClose(toast.id);
            }
          },
          onEscapeKeyDown: (event) => {
            if (!toast.swipeable) {
              event.preventDefault();
            }
          },
          onSwipeMove: (event) => {
            if (!toast.swipeable) {
              event.preventDefault();
            }
          },
          onSwipeEnd: (event) => {
            if (!toast.swipeable) {
              event.preventDefault();
            }
          },
          children: [
            toast.icon ? /* @__PURE__ */ jsx(
              "svg",
              {
                width: "24",
                height: "24",
                viewBox: "0 0 24 24",
                fill: "none",
                xmlns: "http://www.w3.org/2000/svg",
                className: "shrink-0",
                children: /* @__PURE__ */ jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: icon, fill: "currentColor" })
              }
            ) : null,
            /* @__PURE__ */ jsxs("div", { className: "grow space-y-2 text-start", children: [
              /* @__PURE__ */ jsx(ToastPrimitive.Title, { style: { overflowWrap: "anywhere" }, children: toast.title }),
              toast.description && /* @__PURE__ */ jsx(ToastPrimitive.Description, { asChild: true, children: toast.description })
            ] }),
            toast.action && /* @__PURE__ */ jsx(ToastPrimitive.Action, { asChild: true, altText: toast.action.text, children: toast.action.element }),
            toast.dismissible && /* @__PURE__ */ jsx(ToastPrimitive.Close, { asChild: true, children: /* @__PURE__ */ jsx(
              Button_default,
              {
                shape: "text",
                theme: "transparent",
                className: "flex h-8 w-8 shrink-0 items-center justify-center p-1",
                children: /* @__PURE__ */ jsx(
                  "svg",
                  {
                    width: "16",
                    height: "16",
                    viewBox: "0 0 20 20",
                    fill: "none",
                    xmlns: "http://www.w3.org/2000/svg",
                    children: /* @__PURE__ */ jsx(
                      "path",
                      {
                        fillRule: "evenodd",
                        clipRule: "evenodd",
                        d: "M4.16679 2.98828L10.0001 8.82162L15.8335 2.98828L17.012 4.16679L11.1786 10.0001L17.012 15.8335L15.8335 17.012L10.0001 11.1786L4.16679 17.012L2.98828 15.8335L8.82162 10.0001L2.98828 4.16679L4.16679 2.98828Z",
                        fill: "currentColor"
                      }
                    )
                  }
                )
              }
            ) })
          ]
        },
        toast.id
      );
    }),
    /* @__PURE__ */ jsx(
      ToastPrimitive.Viewport,
      {
        className: twMerge(
          VIEWPORT[direction],
          POSITION[pos],
          "fixed z-[2147483647] m-0 flex w-full max-w-sm list-none flex-col gap-[10px] p-[var(--viewport-padding)] outline-none [--viewport-padding:_25px]"
        )
      }
    )
  ] })) });
};
var Toast_default = Toast;

export {
  Toast_default
};
//# sourceMappingURL=chunk-HT4VCWUT.mjs.map